import { Link, useRouterState } from '@tanstack/react-router';
import {
  ChevronDown,
  Landmark,
  Mail,
  Palette,
  Scale,
  ShieldCheck,
  SlidersHorizontal,
  UserCog,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { useClickOutside } from '../lib/use-click-outside';

export const PLATFORM_TABS: Array<{ to: string; label: string; icon: LucideIcon }> = [
  { to: '/platform/organizations', label: 'Vereine', icon: Landmark },
  { to: '/platform/admins', label: 'Plattformadmins', icon: ShieldCheck },
  { to: '/platform/club-admins', label: 'Vereinsadmins', icon: UserCog },
  { to: '/platform/users', label: 'Benutzeraccounts', icon: Users },
  { to: '/platform/design', label: 'Design (Global)', icon: Palette },
  { to: '/platform/mail', label: 'SMTP Server', icon: Mail },
  { to: '/platform/legal', label: 'Impressum & Datenschutz', icon: Scale },
  { to: '/platform/settings', label: 'Erweiterte Einstellungen', icon: SlidersHorizontal },
];

/** Sub-navigation for the "Plattform" admin area — on desktop the same list
 * appears nested under "Plattform" in the sidebar (`AppShell.tsx`), so this
 * in-page control only renders on mobile. Same single-dropdown-button
 * treatment as `OrgTabs` (§ Nutzeranfrage „genauso als Dropdown wie das
 * Vereinsmenü") — replaces the earlier horizontally-scrolling pill row,
 * which hid most of the 8 sections behind a scroll-and-hunt interaction.
 * Kept visually identical to `OrgTabs` so switching between Verein/
 * Plattform on mobile feels like one consistent system rather than two
 * different admin UIs. No permission filtering here (unlike `OrgTabs`) —
 * every route under `/platform` already requires `is_platform_admin`. */
export function PlatformTabs() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  useClickOutside(rootRef, () => setOpen(false), open);

  const active = PLATFORM_TABS.find((t) => t.to === path) ?? PLATFORM_TABS[0];
  const ActiveIcon = active?.icon ?? Landmark;

  return (
    <div className="relative mb-4 md:hidden" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="flex w-full items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-left shadow-sm transition-colors active:bg-slate-50"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-accent/10 text-brand-accent">
          <ActiveIcon size={16} />
        </span>
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">{active?.label}</span>
        <ChevronDown size={18} className={`shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          role="listbox"
          className="org-tabs-dropdown absolute left-0 right-0 z-30 mt-1.5 max-h-[70vh] overflow-y-auto rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl"
        >
          {PLATFORM_TABS.map((t) => {
            const Icon = t.icon;
            const isActive = t.to === path;
            return (
              <Link
                key={t.to}
                to={t.to}
                role="option"
                aria-selected={isActive}
                onClick={() => setOpen(false)}
                className={`flex items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? 'bg-brand-accent/10 text-brand-accent' : 'text-slate-700 hover:bg-slate-50 active:bg-slate-100'
                }`}
              >
                <span
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                    isActive ? 'bg-brand-accent text-white' : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  <Icon size={14} />
                </span>
                {t.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
